import React from 'react';
import $ from 'jquery';
import { debounce, defaultsDeep } from 'lodash';
import { getIsSharedWebpage } from './utils';
import Turbolinks from 'javascripts/turbolinks';
import * as Sentry from '@sentry/browser';
import { createRoot, hydrateRoot } from 'react-dom/client';

const IGNORED_COMPONENTS_WHEN_SHARED_WEBPAGE = [
  // Don't mount the editor if we have prerendered in a shared webpage.
  // Otherwise the editor will reload the HTML, overwrite the prerendered content
  // and if there are embedded views it will cause them to attempt to reload from the server
  'AhaEditorField',
  'AhaEditorFieldV2',
];

const resizeWindow = debounce(() => $(window).trigger('resize'));

const mountNode = async (components, node) => {
  const className = node.getAttribute('data-react-class');
  if (components[className]) {
    const Component = await components[className]();
    const propsJson = node.getAttribute('data-react-props');
    let props = {};

    if (propsJson) {
      try {
        props = JSON.parse(propsJson) || {};
      } catch (error) {
        Sentry.addBreadcrumb({
          type: 'error',
          category: 'react_mount',
          message: `Failed to parse JSON props for component '${className}'`,
          data: {
            componentName: className,
            elementClass: node.className,
          },
        });
        throw error;
      }
    }

    const isSharedWebpage = getIsSharedWebpage();
    if (
      isSharedWebpage &&
      IGNORED_COMPONENTS_WHEN_SHARED_WEBPAGE.includes(className)
    ) {
      return;
    }

    if (
      props.prerendered ||
      (isSharedWebpage && props.hydrateWhenSharedWebpage)
    ) {
      node.__reactRoot = hydrateRoot(node, <Component {...props} />);
    } else {
      if (!node.__reactRoot) {
        node.__reactRoot = createRoot(node);
      }
      node.__reactRoot.render(<Component {...props} />);
    }

    // Resize to account for rendered component changing size.
    resizeWindow();
  } else {
    console.error(
      `Component \`${className}\` cannot be found for mounting; did you add to \`javascripts/application_react_mount\`?`
    );
  }
};

export const patchReactProps = (node, newProps) => {
  const currentProps = JSON.parse(node.dataset.reactProps || '{}');
  const props = {};

  defaultsDeep(props, newProps, currentProps);
  node.dataset.reactProps = JSON.stringify(props);

  window.mountNode(node);
};

export const unmountComponents = $root => {
  // Check if the element is still in the DOM
  if (!$root.length || !$root.get(0).isConnected) {
    return;
  }

  // Catch any potential errors
  Array.from(
    $root
      .find('[data-react-class], [data-dynamic-react-component]')
      .filter(':not([data-turbolinks-permanent])')
  ).forEach(el => {
    try {
      el.__reactRoot?.unmount();
      el.__reactRoot = null;
    } catch (error) {
      // Always log to console
      console.error('React unmount error:', error);

      // Only send to Sentry if it's not the specific "NotFoundError: The object
      // can not be found here" error. That appears to be cause by turbo links
      // transitions but it is not clear how to prevent this error.
      if (
        !(
          error instanceof DOMException &&
          error.name === 'NotFoundError' &&
          (error.message.includes('The object can not be found here') ||
            error.message.includes(
              'The node to be removed is not a child of this node'
            ))
        )
      ) {
        Sentry.captureException(error, {
          extra: {
            reactClass: el.dataset?.reactClass,
            elId: el.id,
            elementClass: el.className,
          },
        });
      }
    }
  });
};

export const addListener = components => {
  window.mountNode = node => mountNode(components, node);

  iripo.in('[data-react-class]', function (node) {
    mountNode(components, node);
  });

  iripo.out('[data-react-class]', function (node) {
    try {
      if (!node.isConnected) {
        node.__reactRoot?.unmount();
      }
    } catch (error) {
      // Always log to console
      console.error('React iripo unmount error:', error);

      // Only send to Sentry if it's not the specific "NotFoundError: The object can not be found here" error
      if (
        !(
          error instanceof DOMException &&
          error.name === 'NotFoundError' &&
          (error.message.includes('The object can not be found here') ||
            error.message.includes(
              'The node to be removed is not a child of this node'
            ))
        )
      ) {
        Sentry.captureException(error);
      }
    }
  });

  $(document).on(Turbolinks.EVENTS.BEFORE_UNLOAD, () =>
    unmountComponents($('body'))
  );
};
